import { aramaMetni } from "./katalog";

/**
 * "Bunu alanlar bunlara da bakıyor" — tamamlayıcı ürün önerileri.
 *
 * Her kural: hangi ürünlere uyar (kategori ve/veya ad) → hangi kategorilerden öneri gelir.
 * İlk uyan kural kullanılır; hiçbiri uymazsa aynı üst gruptaki diğer kategorilerden öneri gelir.
 * Adlar aramaMetni ile sadeleştirilerek karşılaştırılır (ş→s, ı→i …), desenler de öyle yazılmıştır.
 *
 * hedef.ad        → yalnızca adı bu desene uyan ürünler
 * hedef.ayniMarka → yalnızca aynı markanın ürünleri (ör. aküsüz makineye aynı markanın aküsü)
 * hedef.kaynakAd  → bu hedef yalnızca adı bu desene uyan ürünlerde kullanılır
 */

const BOYALAR = ["ic-cephe-boyalari", "dis-cephe-boyalari", "tavan-boyalari"];
const MATKAP_UCLARI = ["beton-matkap-uclari", "ahsap-matkap-uclari", "cam-ve-seramik-matkap-uclari", "silindirik-saplilar"];
const EL_ALETLERI = ["akulu-el-aletleri", "elektrikli-el-aletleri"];
const BOYA_FIRCASI = /kestirme|boya fircasi|fonksiyonel/;
const MATKAP = /matkap|vidalama|kirici|delici/;
const TASLAMA = /taslama|tas motoru/;
const SDS_DEGIL = /^(?!.*sds)/; // kırıcı-delici uçları normal matkaba önerilmesin

const KURALLAR = [
  // Soğuk asfalt → kürek, mala, iş eldiveni, iş ayakkabısı
  {
    kategoriler: ["soguk-asfalt"],
    hedefler: [
      { kat: ["bahce-el-aletleri"], ad: /kurek/ },
      { kat: ["sivaci-aletleri"], ad: /kurek|mala/ },
      { kat: ["el-koruyucular"] },
      { kat: ["ayak-koruyucular"] },
    ],
  },

  // Boya → rulo, fırça, maskeleme bandı, boyacı kabı / sırık, astar
  {
    kategoriler: BOYALAR,
    hedefler: [
      { kat: ["boya-rulolari"] },
      { kat: ["fircalar"], ad: BOYA_FIRCASI },
      { kat: ["bantlar"], ad: /maskeleme/ },
      { kat: ["boya-aksesuarlari"], ad: /boyaci kabi|izgara|sirik|rulo sapi/ },
      { kat: ["astar-ve-macunlar"] },
    ],
  },
  {
    kategoriler: ["astar-ve-macunlar"],
    hedefler: [
      { kat: BOYALAR },
      { kat: ["boya-aksesuarlari", "sivaci-aletleri"], ad: /ispatula|macun/ },
      { kat: ["rulo-zimpalar", "yaprak-zimparalar", "sunger-zimparalar"] },
      { kat: ["boya-rulolari"] },
    ],
  },
  {
    kategoriler: ["endustriyel-boyalar", "hobi-ve-dekoratif-boyalar"],
    hedefler: [
      { kat: ["tiner"] },
      { kat: ["fircalar"], ad: BOYA_FIRCASI },
      { kat: ["boya-rulolari"] },
      { kat: ["bantlar"], ad: /maskeleme/ },
    ],
  },
  {
    kategoriler: ["tiner"],
    hedefler: [{ kat: ["endustriyel-boyalar"] }, { kat: ["fircalar"], ad: BOYA_FIRCASI }],
  },
  {
    kategoriler: ["boya-rulolari", "fircalar", "boya-aksesuarlari"],
    hedefler: [
      { kat: BOYALAR },
      { kat: ["boya-aksesuarlari"], ad: /boyaci kabi|izgara|sirik|rulo sapi/ },
      { kat: ["bantlar"], ad: /maskeleme/ },
      { kat: ["astar-ve-macunlar"] },
    ],
  },

  // Seramik yapıştırıcı ↔ derz dolgu, tarak / seramik malası, silikon
  {
    kategoriler: ["seramik-mermer-yapistiricilari"],
    hedefler: [
      { kat: ["derz-dolgulari"] },
      { kat: ["sivaci-aletleri"], ad: /tarak|seramik malasi|derz malasi/ },
      { kat: ["silikonlar"] },
      { kat: ["su-yalitim-malzemeleri"] },
    ],
  },
  {
    kategoriler: ["derz-dolgulari"],
    hedefler: [
      { kat: ["seramik-mermer-yapistiricilari"] },
      { kat: ["sivaci-aletleri"], ad: /derz|sungerli|tarak/ },
      { kat: ["silikonlar"] },
    ],
  },

  // Aküsüz makine → aynı markanın akü ve şarj cihazı
  {
    kategoriler: EL_ALETLERI,
    ad: /^(?!.*(kirici|delici)).*akusuz/,
    hedefler: [
      { kat: ["akulu-el-aletleri"], ad: /\baku\b|sarj|batarya/, ayniMarka: true },
      { kat: MATKAP_UCLARI, ad: SDS_DEGIL },
      { kat: ["bits-uclar"] },
      { kat: ["goz-koruyucular"] },
    ],
  },
  // Kırıcı-delici → SDS uçlar ve keskiler, gözlük, kulaklık
  {
    kategoriler: EL_ALETLERI,
    ad: /kirici|delici/,
    hedefler: [
      { kat: ["akulu-el-aletleri"], ad: /\baku\b|sarj|batarya/, ayniMarka: true, kaynakAd: /akusuz/ },
      { kat: ["beton-matkap-uclari"], ad: /sds/ },
      { kat: ["keskiler"] },
      { kat: ["goz-koruyucular"] },
      { kat: ["kulak-koruyucular"] },
      { kat: ["solunum-koruyucular"] },
    ],
  },
  // Matkap / vidalama → SDS olmayan uçlar, bits, mandren, gözlük
  {
    kategoriler: EL_ALETLERI,
    ad: MATKAP,
    hedefler: [
      { kat: ["beton-matkap-uclari"], ad: SDS_DEGIL },
      { kat: ["bits-uclar"] },
      { kat: ["ahsap-matkap-uclari", "cam-ve-seramik-matkap-uclari"] },
      { kat: ["mandrenler"], ad: SDS_DEGIL },
      { kat: ["goz-koruyucular"] },
    ],
  },
  // Taşlama → kesme / taşlama diskleri, flap disk, yüz ve göz koruyucu
  {
    kategoriler: EL_ALETLERI,
    ad: TASLAMA,
    hedefler: [
      { kat: ["kesisi-capak-alma-diskleri"] },
      { kat: ["kesici-elmas-diskler"] },
      { kat: ["flap-diskler"] },
      { kat: ["goz-koruyucular", "yuz-koruyucular"] },
      { kat: ["el-koruyucular"] },
    ],
  },
  {
    kategoriler: EL_ALETLERI,
    ad: /dekupaj|testere|sunta kesme|tilki|kesme/,
    hedefler: [
      { kat: ["testere-bicaklari-ve-delik-acma-testereleri"] },
      { kat: ["goz-koruyucular"] },
      { kat: ["el-koruyucular"] },
    ],
  },
  {
    kategoriler: EL_ALETLERI,
    ad: /zimpara|polisaj/,
    hedefler: [
      { kat: ["cirtli-diskler"] },
      { kat: ["yaprak-zimparalar", "rulo-zimpalar", "bant-zimparalar"] },
      { kat: ["solunum-koruyucular"] },
    ],
  },
  {
    kategoriler: EL_ALETLERI,
    ad: /somun sikma/,
    hedefler: [{ kat: ["lokma-ve-bijon-anahtarlari"] }, { kat: ["el-koruyucular"] }],
  },
  {
    kategoriler: EL_ALETLERI,
    ad: /freze|planya/,
    hedefler: [{ kat: ["freze-uclari-ve-palanya-bicaklari"] }, { kat: ["goz-koruyucular"] }],
  },
  {
    kategoriler: EL_ALETLERI,
    hedefler: [
      { kat: ["bits-uclar"] },
      { kat: ["takim-cantalari-ve-atolye-dolaplari"] },
      { kat: ["goz-koruyucular"] },
      { kat: ["el-koruyucular"] },
    ],
  },

  // Uçlar ve diskler → uygun makine
  {
    kategoriler: [...MATKAP_UCLARI, "mandrenler"],
    hedefler: [
      { kat: EL_ALETLERI, ad: MATKAP },
      { kat: ["bits-uclar"] },
      { kat: ["goz-koruyucular"] },
    ],
  },
  {
    kategoriler: ["bits-uclar"],
    hedefler: [
      { kat: ["akulu-el-aletleri"], ad: /vidalama/ },
      { kat: ["tornavidalar-ve-allen-anahtarlar"] },
      { kat: MATKAP_UCLARI },
    ],
  },
  {
    kategoriler: ["kesisi-capak-alma-diskleri", "kesici-elmas-diskler", "flap-diskler", "fiber-diskler", "tel-fircalar"],
    hedefler: [
      { kat: EL_ALETLERI, ad: TASLAMA },
      { kat: ["goz-koruyucular", "yuz-koruyucular"] },
      { kat: ["el-koruyucular"] },
      { kat: ["kulak-koruyucular"] },
    ],
  },
  {
    kategoriler: ["testere-bicaklari-ve-delik-acma-testereleri"],
    hedefler: [{ kat: EL_ALETLERI, ad: /dekupaj|testere|kesme/ }, { kat: ["goz-koruyucular"] }],
  },
  {
    kategoriler: ["freze-uclari-ve-palanya-bicaklari"],
    hedefler: [{ kat: EL_ALETLERI, ad: /freze|planya/ }, { kat: ["goz-koruyucular"] }],
  },

  // Kaynak
  {
    kategoriler: ["ortulu-elektrod-kaynak-makinesi", "argon-tig-kaynak-makinalari", "plazma-kesme-makinalari"],
    hedefler: [
      { kat: ["ortulu-elektrodlar", "argon-kaynak-telleri", "gazalti-kaynak-telleri"] },
      { kat: ["kaynakci-guvenlik-ekipmanlari"] },
      { kat: ["torclar", "kaynak-yardimci-ekipmanlari"] },
      { kat: ["el-koruyucular"] },
    ],
  },
  {
    kategoriler: ["ortulu-elektrodlar", "ortulu-kaynak-elektrodlari", "argon-kaynak-telleri", "gazalti-kaynak-telleri", "gazalti-mig-mag-kaynak-telleri", "toz-alti-kaynak-telleri", "torclar"],
    hedefler: [
      { kat: ["ortulu-elektrod-kaynak-makinesi", "argon-tig-kaynak-makinalari"] },
      { kat: ["kaynakci-guvenlik-ekipmanlari"] },
      { kat: ["kesisi-capak-alma-diskleri"] },
    ],
  },

  // Sızdırmazlık → tabanca, eldiven
  {
    kategoriler: ["silikonlar", "mastikler", "montaj-yapistiricilari", "pu-kopukler"],
    hedefler: [
      { kat: ["silikon-ve-kopuk-tabancalari"] },
      { kat: ["silikonlar", "mastikler", "pu-kopukler", "montaj-yapistiricilari"] },
      { kat: ["maket-bicaklari"] },
      { kat: ["el-koruyucular"] },
    ],
  },
  {
    kategoriler: ["silikon-ve-kopuk-tabancalari"],
    hedefler: [{ kat: ["silikonlar"] }, { kat: ["pu-kopukler"] }, { kat: ["mastikler", "montaj-yapistiricilari"] }],
  },

  // Duvar: tuğla / gazbeton / çimento → harç, mala, su terazisi
  {
    kategoriler: ["tuglalar", "gazbeton"],
    hedefler: [
      { kat: ["cimento-esasli-sivalar-ve-orgu-harclari"] },
      { kat: ["cimento"] },
      { kat: ["sivaci-aletleri"], ad: /duvarci|tugla|mala/ },
      { kat: ["su-terazisi"] },
    ],
  },
  {
    kategoriler: ["cimento", "grout-beton-ve-harc-katkilari", "cimento-esasli-sivalar-ve-orgu-harclari"],
    hedefler: [
      { kat: ["cimento", "cimento-esasli-sivalar-ve-orgu-harclari"] },
      { kat: ["grout-beton-ve-harc-katkilari"] },
      { kat: ["sivaci-aletleri"], ad: /mala|kurek|kova|tekne/ },
      { kat: ["tuglalar", "gazbeton"] },
    ],
  },

  // Alçı / sıva / alçıpan
  {
    kategoriler: ["alci-ve-siva-urunleri", "siva-alci-malzemeleri"],
    hedefler: [
      { kat: ["sivaci-aletleri"], ad: /mala|tekne|kova|mastar/ },
      { kat: ["astar-ve-macunlar"] },
      { kat: ["alci-ve-siva-urunleri", "siva-alci-malzemeleri"] },
    ],
  },
  {
    kategoriler: ["alcipan-ve-kuru-duvar-sistemleri", "asma-tavan-sistemleri"],
    hedefler: [
      { kat: ["alci-ve-siva-urunleri"] },
      { kat: ["astar-ve-macunlar"] },
      { kat: ["akulu-el-aletleri"], ad: /vidalama/ },
      { kat: ["bits-uclar"] },
    ],
  },

  // Yalıtım
  {
    kategoriler: ["su-yalitim-malzemeleri"],
    hedefler: [
      { kat: ["fircalar"], ad: BOYA_FIRCASI },
      { kat: ["boya-rulolari"] },
      { kat: ["sivaci-aletleri"], ad: /mala/ },
      { kat: ["el-koruyucular"] },
    ],
  },
  {
    kategoriler: ["isi-yalitim-urunleri", "yalitim-malzemeleri"],
    hedefler: [
      { kat: ["pu-kopukler", "montaj-yapistiricilari"] },
      { kat: ["sivaci-aletleri"], ad: /mala|tarak/ },
      { kat: ["maket-bicaklari"] },
    ],
  },

  // Tesisat
  {
    kategoriler: ["banyo-bataryalari", "lavabo-bataryalari", "dus-setleri"],
    hedefler: [
      { kat: ["silikonlar"] },
      { kat: ["boru-anahtarlari-ve-penseleri"] },
      { kat: ["banyo-bataryalari", "lavabo-bataryalari", "dus-setleri"] },
    ],
  },
  {
    kategoriler: ["pp-r-boru-sistemleri", "pex-b-borular", "pvc-atik-su-borulari", "dogalgaz-borulari"],
    hedefler: [{ kat: ["boru-makasi-ve-kesiciler"] }, { kat: ["boru-anahtarlari-ve-penseleri"] }, { kat: ["silikonlar"] }],
  },

  // Elektrik
  {
    kategoriler: ["elektrik-anahtarlari", "prizler", "grup-prizler", "anahtar-priz-aksesuarlari", "aydinlatma-urunleri", "serit-led-urunler"],
    hedefler: [
      { kat: ["enerji-kablolari"] },
      { kat: ["tornavidalar-ve-allen-anahtarlar"] },
      { kat: ["kacak-akim-koruma-salterleri", "otomatik-sigortalar"] },
      { kat: ["elektrik-anahtarlari", "prizler"] },
    ],
  },

  // Mekanik el aletleri → takım çantası, eldiven
  {
    kategoriler: [
      "tornavidalar-ve-allen-anahtarlar",
      "pense-ve-kargaburunlar",
      "anahtarlar",
      "lokma-ve-bijon-anahtarlari",
      "keskiler-ve-cekicler",
      "servis-alet-ve-ekipmanlari",
      "cektirmeler",
      "makaslar",
      "kesici-delici-el-aletleri",
    ],
    hedefler: [
      { kat: ["takim-cantalari-ve-atolye-dolaplari"] },
      { kat: ["tornavidalar-ve-allen-anahtarlar", "pense-ve-kargaburunlar", "anahtarlar"] },
      { kat: ["el-koruyucular"] },
    ],
  },
];

// Aynı ürün her açılışta aynı önerileri görsün, farklı ürünler farklı önerileri görsün
const tohum = (metin) => {
  let h = 0;
  for (let i = 0; i < metin.length; i++) h = (h * 31 + metin.charCodeAt(i)) >>> 0;
  return h;
};

/**
 * Ürüne tamamlayıcı öneriler — her hedef gruptan sırayla birer ürün alınır.
 * Aynı markanın ürünleri öne çıkar (Filli boyaya Filli astar gibi).
 */
export function tamamlayicilar(urun, urunler, dizin, sinir = 8) {
  if (!urun || !urunler?.length) return [];
  const ad = aramaMetni(urun.ad);
  const kural = KURALLAR.find((k) => k.kategoriler.includes(urun.kategori) && (!k.ad || k.ad.test(ad)));

  let hedefler = kural?.hedefler;
  if (!hedefler) {
    // Kural yoksa: aynı üst gruptaki diğer kategoriler
    const ust = dizin?.[urun.kategori];
    const kardesler = ust
      ? [...new Set(urunler.map((u) => u.kategori))].filter((k) => k !== urun.kategori && dizin[k] === ust)
      : [];
    hedefler = kardesler.map((k) => ({ kat: [k] }));
  }
  if (!hedefler.length) return [];

  const t = tohum(urun.id);
  const gruplar = hedefler
    .filter((h) => !h.kaynakAd || h.kaynakAd.test(ad))
    .map((h) => {
      const liste = urunler.filter(
        (u) =>
          u.id !== urun.id &&
          h.kat.includes(u.kategori) &&
          (!h.ad || h.ad.test(aramaMetni(u.ad))) &&
          (!h.ayniMarka || u.marka === urun.marka)
      );
      if (!liste.length) return liste;
      // Döndürerek karıştır, sonra aynı markayı öne al
      const k = t % liste.length;
      const donmus = [...liste.slice(k), ...liste.slice(0, k)];
      return [...donmus.filter((u) => u.marka === urun.marka), ...donmus.filter((u) => u.marka !== urun.marka)];
    })
    .filter((g) => g.length);

  const secilen = [];
  const gorulen = new Set();
  for (let tur = 0; secilen.length < sinir && gruplar.some((g) => g.length > tur); tur++) {
    for (const g of gruplar) {
      const u = g[tur];
      if (u && !gorulen.has(u.id)) {
        gorulen.add(u.id);
        secilen.push(u);
        if (secilen.length >= sinir) break;
      }
    }
  }
  return secilen;
}
